import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FolderOpen, Globe, Key,
  AlertTriangle, Code2, Settings, Search,
  ChevronLeft, ChevronRight, Zap, Boxes, Wallet, BookOpen,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUIStore } from '@/store/useUIStore';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { NAV_ITEMS, BOTTOM_NAV_ITEMS } from '@/constants';

const ICON_MAP = {
  LayoutDashboard, FolderOpen, Globe, Key,
  AlertTriangle, Code2, Settings, Search, Boxes, Wallet,
};

function NavItem({ item, collapsed }) {
  const Icon = ICON_MAP[item.icon] ?? BookOpen;
  const location = useLocation();
  const isActive = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);

  const link = (
    <NavLink
      to={item.path}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
        'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent',
        isActive && 'bg-sidebar-accent text-sidebar-foreground shadow-sm'
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden whitespace-nowrap flex items-center gap-2 flex-1"
          >
            {item.label}
            {item.badge && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-primary/20 text-primary font-semibold tracking-wide leading-none">
                {item.badge}
              </span>
            )}
          </motion.span>
        )}
      </AnimatePresence>
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }

  return link;
}

export default function Sidebar() {
  const { sidebarOpen, toggleSidebar } = useUIStore();

  return (
    <motion.aside
      animate={{ width: sidebarOpen ? 240 : 60 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="relative flex flex-col bg-sidebar border-r border-sidebar-border h-full overflow-hidden shrink-0"
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-3 py-4 border-b border-sidebar-border">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary shrink-0">
          <Zap className="h-4 w-4 text-primary-foreground" />
        </div>
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <p className="text-sm font-semibold text-sidebar-foreground leading-none">Albecollect</p>
              <p className="text-xs text-sidebar-foreground/50 mt-0.5">Developer Portal</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.id} item={item} collapsed={!sidebarOpen} />
        ))}
      </nav>

      {/* Bottom nav */}
      <div className="border-t border-sidebar-border py-3 px-2 space-y-0.5">
        {BOTTOM_NAV_ITEMS.map((item) => (
          <NavItem key={item.id} item={item} collapsed={!sidebarOpen} />
        ))}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar shadow-sm hover:bg-sidebar-accent transition-colors"
      >
        {sidebarOpen ? (
          <ChevronLeft className="h-3.5 w-3.5 text-sidebar-foreground/70" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-sidebar-foreground/70" />
        )}
      </button>
    </motion.aside>
  );
}
